import { supabase } from '../lib/supabase';
import { EmpresaConfig, EMPRESA_PADRAO_INICIAL, EMPRESA_TWO_INICIAL } from '../types/empresa';
import { storageService } from './storageService';

const PREFERENCE_KEY_LISTA = 'empresa_config_lista';
const PREFERENCE_KEY_ATIVA = 'empresa_config_ativa_id';
const LOCAL_STORAGE_KEY_LISTA = 'voope_empresas_config_lista_v1';
const LOCAL_STORAGE_KEY_ATIVA = 'voope_empresa_ativa_id_v1';

export const empresaService = {
  /**
   * Obtém a lista de todas as empresas cadastradas
   */
  async getEmpresas(): Promise<EmpresaConfig[]> {
    try {
      // 1. Tentar ler do Supabase (system_preference)
      const { data, error } = await supabase
        .from('system_preference')
        .select('preference')
        .eq('id', PREFERENCE_KEY_LISTA)
        .maybeSingle();

      if (!error && data?.preference) {
        try {
          const parsed = JSON.parse(data.preference);
          if (Array.isArray(parsed) && parsed.length > 0) {
            localStorage.setItem(LOCAL_STORAGE_KEY_LISTA, JSON.stringify(parsed));
            return parsed;
          }
        } catch (e) {
          console.warn('[empresaService] Falha ao fazer parse do preference:', e);
        }
      }

      // 2. Fallback para localStorage
      const cached = localStorage.getItem(LOCAL_STORAGE_KEY_LISTA);
      if (cached) {
        try {
          const parsed = JSON.parse(cached);
          if (Array.isArray(parsed) && parsed.length > 0) {
            return parsed;
          }
        } catch (e) {
          console.warn('[empresaService] Falha ao fazer parse do localStorage:', e);
        }
      }

      // 3. Fallback inicial com as duas empresas padrão da Voope
      const defaultList = [EMPRESA_PADRAO_INICIAL, EMPRESA_TWO_INICIAL];
      await this.saveAllEmpresas(defaultList);
      return defaultList;
    } catch (err) {
      console.error('[empresaService] Erro ao buscar empresas:', err);
      return [EMPRESA_PADRAO_INICIAL, EMPRESA_TWO_INICIAL];
    }
  },

  /**
   * Obtém a empresa atualmente ativa/padrão
   */
  async getEmpresaAtiva(): Promise<EmpresaConfig> {
    const empresas = await this.getEmpresas();
    const ativaId = localStorage.getItem(LOCAL_STORAGE_KEY_ATIVA);
    
    if (ativaId) {
      const encontrada = empresas.find(e => e.id === ativaId);
      if (encontrada) return encontrada;
    }

    const padrao = empresas.find(e => e.is_padrao) || empresas[0] || EMPRESA_PADRAO_INICIAL;
    return padrao;
  },

  /**
   * Define qual empresa é a ativa no momento
   */
  async setEmpresaAtiva(empresaId: string): Promise<void> {
    localStorage.setItem(LOCAL_STORAGE_KEY_ATIVA, empresaId);

    try {
      await supabase
        .from('system_preference')
        .upsert({
          id: PREFERENCE_KEY_ATIVA,
          preference: empresaId
        });
    } catch (e) {
      console.warn('[empresaService] Erro ao persistir empresa ativa no banco:', e);
    }

    // Dispara evento para atualização instantânea na interface (Header, Sidebar, etc)
    window.dispatchEvent(new CustomEvent('empresa_config_updated', { detail: { empresaId } }));
  },

  /**
   * Salva ou atualiza os dados de uma empresa específica
   */
  async saveEmpresa(empresa: EmpresaConfig): Promise<{ success: boolean; data?: EmpresaConfig; error?: string }> {
    try {
      if (!empresa.razao_social?.trim()) {
        return { success: false, error: 'A Razão Social é obrigatória.' };
      }
      if (!empresa.cnpj?.trim()) {
        return { success: false, error: 'O CNPJ é obrigatório.' };
      }

      const empresas = await this.getEmpresas();
      const index = empresas.findIndex(e => e.id === empresa.id);

      const agora = new Date().toISOString();
      const empresaParaSalvar: EmpresaConfig = {
        ...empresa,
        updated_at: agora,
        created_at: empresa.created_at || agora
      };

      let novaLista: EmpresaConfig[];
      if (index >= 0) {
        novaLista = [...empresas];
        novaLista[index] = empresaParaSalvar;
      } else {
        novaLista = [...empresas, empresaParaSalvar];
      }

      // Se esta empresa foi marcada como padrão, desmarca as outras
      if (empresaParaSalvar.is_padrao) {
        novaLista = novaLista.map(e => ({
          ...e,
          is_padrao: e.id === empresaParaSalvar.id
        }));
      }

      const saveRes = await this.saveAllEmpresas(novaLista);
      if (!saveRes.success) {
        return { success: false, error: saveRes.error || 'Erro ao persistir no banco de dados.' };
      }

      // Dispara evento de atualização
      window.dispatchEvent(new CustomEvent('empresa_config_updated', { detail: { empresa: empresaParaSalvar } }));

      return { success: true, data: empresaParaSalvar };
    } catch (err: any) {
      console.error('[empresaService] Erro ao salvar empresa:', err);
      return { success: false, error: err.message || 'Erro ao salvar os dados da empresa.' };
    }
  },

  /**
   * Salva a lista completa no banco de dados estrutural (tabela system_preference) e cache local
   */
  async saveAllEmpresas(lista: EmpresaConfig[]): Promise<{ success: boolean; error?: string }> {
    const json = JSON.stringify(lista);
    localStorage.setItem(LOCAL_STORAGE_KEY_LISTA, json);

    try {
      // 1. Grava no banco de dados estrutural existente (tabela system_preference do Supabase)
      const { error } = await supabase
        .from('system_preference')
        .upsert({
          id: PREFERENCE_KEY_LISTA,
          preference: json
        });

      if (error) {
        console.error('[empresaService] Erro ao gravar no banco de dados (system_preference):', error);
        return { success: false, error: `Erro no banco de dados: ${error.message}` };
      }

      // 2. Sincroniza com as unidades do sistema (system_unit) se aplicável
      try {
        const matriz = lista.find(e => e.id === 'rg_da_silva' || e.id === 'voope_matriz' || e.is_padrao) || lista[0];
        if (matriz?.nome_fantasia) {
          await supabase
            .from('system_unit')
            .update({ name: matriz.nome_fantasia })
            .eq('id', 1);
        }

        const filial = lista.find(e => e.id === 'voope_two') || (lista.length > 1 ? lista[1] : null);
        if (filial?.nome_fantasia) {
          await supabase
            .from('system_unit')
            .update({ name: filial.nome_fantasia })
            .eq('id', 2);
        }
      } catch (unitErr) {
        // system_unit sync é complementar
        console.debug('[empresaService] Atualização complementar de system_unit:', unitErr);
      }

      return { success: true };
    } catch (err: any) {
      console.warn('[empresaService] Falha de comunicação com o banco de dados:', err);
      return { success: false, error: err.message || 'Falha ao salvar no banco de dados.' };
    }
  },

  /**
   * Exclui uma empresa cadastrada
   */
  async deleteEmpresa(id: string): Promise<{ success: boolean; error?: string }> {
    try {
      const empresas = await this.getEmpresas();
      if (empresas.length <= 1) {
        return { success: false, error: 'Não é possível excluir a única empresa cadastrada no sistema.' };
      }

      const novaLista = empresas.filter(e => e.id !== id);
      // Se a excluída era padrão, define a primeira como padrão
      if (!novaLista.some(e => e.is_padrao) && novaLista.length > 0) {
        novaLista[0].is_padrao = true;
      }

      await this.saveAllEmpresas(novaLista);

      // Se a excluída era a ativa no localStorage, muda para a padrão ou primeira
      const ativaId = localStorage.getItem(LOCAL_STORAGE_KEY_ATIVA);
      if (ativaId === id) {
        const novaAtiva = novaLista.find(e => e.is_padrao) || novaLista[0];
        if (novaAtiva) {
          localStorage.setItem(LOCAL_STORAGE_KEY_ATIVA, novaAtiva.id);
        }
      }

      window.dispatchEvent(new CustomEvent('empresa_config_updated', { detail: { deletedId: id } }));

      return { success: true };
    } catch (err: any) {
      return { success: false, error: err.message || 'Erro ao excluir empresa.' };
    }
  },

  /**
   * Upload de imagem com fallback resiliente para Base64 Data URL
   */
  async uploadLogo(file: File): Promise<{ success: boolean; url?: string; error?: string }> {
    try {
      // 1. Validações preliminares
      const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        return { success: false, error: 'Formato inválido. Envie um arquivo PNG, JPG, SVG ou WEBP.' };
      }

      if (file.size > 5 * 1024 * 1024) {
        return { success: false, error: 'A imagem deve ter no máximo 5MB.' };
      }

      // 2. Tentar upload no Supabase Storage
      try {
        const uploadResult = await storageService.uploadFile(file, 'geral', `logo_empresa_${Date.now()}.${file.name.split('.').pop()}`);
        if (uploadResult?.publicUrl) {
          return { success: true, url: uploadResult.publicUrl };
        }
      } catch (storageErr) {
        console.warn('[empresaService] Storage upload falhou, usando Base64 fallback:', storageErr);
      }

      // 3. Fallback: Converter para Base64
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = (e) => reject(e);
        reader.readAsDataURL(file);
      });

      return { success: true, url: base64 };
    } catch (err: any) {
      console.error('[empresaService] Erro ao processar logo:', err);
      return { success: false, error: err.message || 'Erro ao processar a imagem da logo.' };
    }
  },

  /**
   * Busca endereço automaticamente por CEP
   */
  async buscarCep(cep: string): Promise<{
    logradouro?: string;
    bairro?: string;
    cidade?: string;
    uf?: string;
    ibge?: string;
    error?: string;
  }> {
    const cleanCep = cep.replace(/\D/g, '');
    if (cleanCep.length !== 8) {
      return { error: 'CEP deve conter 8 dígitos numéricos.' };
    }

    try {
      const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
      const data = await response.json();

      if (data.erro) {
        return { error: 'CEP não localizado na base dos Correios.' };
      }

      return {
        logradouro: data.logradouro || '',
        bairro: data.bairro || '',
        cidade: data.localidade || '',
        uf: data.uf || '',
        ibge: data.ibge || ''
      };
    } catch (err: any) {
      console.error('[empresaService] Erro ao consultar ViaCEP:', err);
      return { error: 'Não foi possível consultar o CEP no momento.' };
    }
  }
};
